<script lang="ts">
    import Chart from 'chart.js/auto'
    import type { Plugin } from 'chart.js'
    import ChartDataLabels from 'chartjs-plugin-datalabels'
    import { onMount } from 'svelte'
    import { InvoicesRepository } from '../../modules/posApi/invoices/invoices.repository'
    import { InvoicesService } from '../../modules/posApi/invoices/invoices.service'
    import dayjs from 'dayjs'
    import utc from 'dayjs/plugin/utc'
    dayjs.extend(utc)
    import { PersonsRepository } from '../../modules/eventsApi/persons/persons.repository'
    import { PersonsService } from '../../modules/eventsApi/persons/persons.service'
    import { PaymentMethodsRepository } from '../../modules/posApi/payment-methods/payment-methods.repository'
    import { PaymentMethodsService } from '../../modules/posApi/payment-methods/payment-methods.service'
    import { ServicesRepository } from '../../modules/posApi/services/services.repository'
    import { ServicesService } from '../../modules/posApi/services/services.service'
    import { AppointmentsRepository } from '../../modules/schedulerApi/appointments/appointments.repository'
    import { AppointmentsService } from '../../modules/schedulerApi/appointments/appointments.service'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import ZContainerCol from '../_common/z/z-container-col.svelte'

    let faturamentoOntem: number = 0
    let faturamentoHoje: number = 0
    let avaliacoesAgendadasAnterior: number = 0
    let avaliacoesAgendadasAtual: number = 0
    let avaliacoesRealizadasAnterior: number = 0
    let avaliacoesRealizadasAtual: number = 0
    let avaliacoesConvertidasAnterior: number = 0
    let avaliacoesConvertidasAtual: number = 0
    let desempenho: string = '...'
    let show = false

    let chartEvolution: HTMLCanvasElement
    let chartAvaliacoes: HTMLCanvasElement
    let chartVendasDia: HTMLCanvasElement
    let chartVendasMes: HTMLCanvasElement
    let chartOrigem: HTMLCanvasElement
    let chartMeioPagamento: HTMLCanvasElement
    let chartMaisVendidos: HTMLCanvasElement

    let charts: Chart[] = []
    const MUTED = '#666'

    let maisVendidosHeight = 380

    const posR = new InvoicesRepository()
    const posS = new InvoicesService(posR)

    let personsR = new PersonsRepository()
    let personsS = new PersonsService(personsR)

    let pmR = new PaymentMethodsRepository()
    let pmS = new PaymentMethodsService(pmR)

    let servicesR = new ServicesRepository()
    let servicesS = new ServicesService(servicesR)

    let appointmentsR = new AppointmentsRepository()
    let appointmentsS = new AppointmentsService(appointmentsR)

    async function loadDashboard() {
        // getting data
        let consultingServicesId = []
        // get all services
        const services = await servicesS.getAll(undefined, 600)
        // get consulting
        for (const service of services) {
            if (service.consulting && !consultingServicesId.find((el) => el === service.serviceId)) {
                consultingServicesId.push(service.serviceId)
            }
        }

        evaluateDays()

        await Promise.all([loadTopCharts(), loadConsultings(consultingServicesId)])
        await loadOtherCharts(services)
    }

    let fromDay
    let toDay
    let fromMonth
    let lastDayOfMonth
    let toDayBack
    let fromDayBack
    let thisMonth
    function evaluateDays() {
        // dates
        fromMonth = new Date()
        fromMonth.setMonth(fromMonth.getMonth() - 18)
        fromMonth.setDate(1)
        fromMonth.setHours(0, 0, 0, 0)
        thisMonth = window.api.utils.dates.currentMonth()
        lastDayOfMonth = thisMonth.to
        // all other data sources
        fromDay = new Date()
        toDay = new Date()
        toDay.setHours(23, 59, 59)
        fromDay.setDate(fromDay.getDate() - 27) // -27 para finais de semana baterem na comparação. e não 28 pois o horário é diferente 00:00 e 23:59
        fromDay.setHours(0, 0, 0, 0)
        toDayBack = new Date(toDay) // não precisa subtrair 1, pois começa meia-noite
        toDayBack.setDate(toDayBack.getDate() - 28)
        fromDayBack = new Date(toDayBack)
        fromDayBack.setDate(fromDayBack.getDate() - 27)
        fromDayBack.setHours(0, 0, 0, 0)
    }

    let evolution
    let totalByDay
    async function loadTopCharts() {
        ;[totalByDay, evolution] = await Promise.all([posS.getTotalByDay(fromDay, toDay), posS.getEvolution()])

        //// evolution / ontem VS hoje e 3 meses recentes
        showChartEvolution(evolution.current, evolution.average)

        // - ontem e hoje
        let today = new Date()
        today.setHours(0, 0, 0, 0)
        let yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        yesterday.setHours(0, 0, 0, 0)
        faturamentoOntem = totalByDay.find((el) => el.date.split('T')[0] === yesterday.toJSON().split('T')[0])?.total || 0
        faturamentoHoje = totalByDay.find((el) => el.date.split('T')[0] === today.toJSON().split('T')[0])?.total || 0
    }

    async function loadOtherCharts(services) {
        const [totalByMonth, totalByDayPast, currentMonth, payments] = await Promise.all([
            posS.getTotalByMonth(fromMonth, lastDayOfMonth),

            posS.getTotalByDay(fromDayBack, toDayBack),

            posS.getAll({
                from: thisMonth.from,
                to: thisMonth.to,
                service: true,
                payment: true,
                status: ['closed', 'subsidized', 'partial', 'open', 'credit'],
            }),
            pmS.getAll(),
        ])
        //

        // get all persons
        let personsId = []
        for (const invoice of currentMonth) {
            if (!personsId.includes(invoice.personId)) personsId.push(invoice.personId)
        }
        let promises = []
        for (let i = 0; i < personsId.length; i += 100) {
            const chunk = personsId.slice(i, i + 100)
            promises.push(personsS.getAll({ persons: chunk.join(',') }, 1800))
        }
        const results = await Promise.all(promises)
        let persons = []
        for (const result of results) {
            persons = persons.concat(result)
        }

        // attach persons, services and payment mothods into invoices
        for (let invoice of currentMonth) {
            invoice.person = persons.find((el) => el.personId === invoice.personId)
            for (let service of invoice.services) {
                service.service = services.find((el) => el.serviceId === service.serviceId)
            }
            for (let payment of invoice.payments) {
                payment.paymentMethod = payments.find((el) => el.paymentMethodId === payment.paymentMethodId)
            }
        }

        // vendas por mes
        let data = []
        let label = []
        for (let date = fromMonth; date <= new Date(); date.setMonth(date.getMonth() + 1)) {
            const el = totalByMonth.find((el) => String(el.date).substring(0, 7) === date.toJSON().substring(0, 7))
            data.push(Number(el?.total.toFixed(0) || 0))
            label.push(dayjs(date).format('MM/YY'))
        }
        showChartVendasMes(data, label)

        // vendas por dia (últimos 28 dias x período anterior)
        data = []
        label = []
        for (let date = new Date(fromDay); date <= new Date(); date.setDate(date.getDate() + 1)) {
            const el = totalByDay.find((el) => String(el.date).substring(0, 10) === date.toJSON().substring(0, 10))
            data.push(Number(el?.total.toFixed(0) || 0))
            label.push(dayjs(date).format('DD/MM'))
        }
        let dataPast = []
        for (let date = new Date(fromDayBack); date <= toDayBack; date.setDate(date.getDate() + 1)) {
            const el = totalByDayPast.find((el) => String(el.date).substring(0, 10) === date.toJSON().substring(0, 10))
            dataPast.push(Number(el?.total.toFixed(0) || 0))
        }
        showChartVendasDia(data, dataPast, label)

        // mais vendidos
        let maisVendidos = []
        for (const invoice of currentMonth) {
            for (const service of invoice.services) {
                let item = maisVendidos.find((el) => el.label === service.service?.name)
                if (!item) {
                    item = { label: service.service?.name || 'Não informado', total: 0 }
                    maisVendidos.push(item)
                }
                item.total += service.quantity * service.value
            }
        }
        maisVendidos.sort(dynamicSortMultiple(true, 'total'))
        maisVendidos = maisVendidos.slice(0, 15)
        showChartMaisVendidos(maisVendidos)

        // pela origem de pagamento
        let pelaOrigem = []
        for (const invoice of currentMonth) {
            let label = invoice.person?.referral?.label || 'Não informado'
            let item = pelaOrigem.find((el) => el.label === label)
            if (!item) {
                item = { label: label, total: 0 }
                pelaOrigem.push(item)
            }
            item.total += invoice.total
        }
        pelaOrigem.sort(dynamicSortMultiple(true, 'total'))
        showChartOrigem(pelaOrigem)

        // pelo meio de pagamento
        let meioDePagamento = []
        for (const invoice of currentMonth) {
            for (const payment of invoice.payments) {
                const label = payment.paymentMethod?.label || 'Não informado'
                let item = meioDePagamento.find((el) => el.label === label)
                if (!item) {
                    item = { label, total: 0 }
                    meioDePagamento.push(item)
                }
                item.total += payment.value
            }
        }
        meioDePagamento.sort(dynamicSortMultiple(true, 'total'))
        showChartMeioPagamento(meioDePagamento)

        show = false
    }

    async function loadConsultings(consultingServicesId) {
        ///// consulting
        let consultingFrom = new Date()
        consultingFrom.setHours(0, 0, 0, 0)
        consultingFrom.setDate(1)
        consultingFrom.setMonth(consultingFrom.getMonth() - 1)
        let consultingTo = new Date()
        consultingTo.setHours(0, 0, 0, 0)
        consultingTo.setDate(consultingTo.getDate() + 8)

        // total de avaliações / comparativo
        // a busca de avaliações devolve uma linha por serviço de avaliação dentro do agendamento e não traz o id
        // do agendamento; pessoa + horário identificam o agendamento, então as linhas repetidas são descartadas
        // para contar agendamentos, não itens
        const consultingRows = await appointmentsS.getConsulting(consultingFrom, consultingTo, consultingServicesId.join(','))
        const seenAppointments = new Set<string>()
        const consultings = consultingRows.filter((el) => {
            const key = el.personId + '|' + el.start
            if (seenAppointments.has(key)) return false
            seenAppointments.add(key)
            return true
        })

        // vendas usadas para checar conversão: mesma data do agendamento até 30 dias depois
        let conversionTo = new Date(consultingTo)
        conversionTo.setDate(conversionTo.getDate() + 30)
        const conversionInvoices = await posS.getAll({
            from: consultingFrom,
            to: conversionTo,
            status: ['closed', 'subsidized', 'partial', 'open', 'credit'],
        })

        let today = new Date()
        today.setHours(0, 0, 0, 0)

        let limitFrom = new Date()
        limitFrom.setDate(limitFrom.getDate() - 7)
        let limitTo = new Date()
        limitTo.setDate(limitTo.getDate() + 7)
        limitTo.setHours(23, 59, 59, 999)
        let dadosAv = []
        for (const item of consultings) {
            const current = new Date(item.start)
            if (current.getDate() <= today.getDate() && (current.getMonth() < today.getMonth() || current.getFullYear() < today.getFullYear())) {
                if (item.status !== 'agendamento_excluido') {
                    avaliacoesAgendadasAnterior += 1
                }
                if (item.status === 'agendamento_compareceu') {
                    avaliacoesRealizadasAnterior += 1
                    if (wasConverted(item, conversionInvoices)) avaliacoesConvertidasAnterior += 1
                }
            }
            if (current.getDate() <= today.getDate() && current.getMonth() === today.getMonth()) {
                if (item.status !== 'agendamento_excluido') {
                    avaliacoesAgendadasAtual += 1
                }
                if (item.status === 'agendamento_compareceu') {
                    avaliacoesRealizadasAtual += 1
                    if (wasConverted(item, conversionInvoices)) avaliacoesConvertidasAtual += 1
                }
            }
        }

        // gráfico de avaliações
        console.log('limitTo', limitTo, consultings)
        for (let date = limitFrom; date <= limitTo; date.setDate(date.getDate() + 1)) {
            dadosAv.push({ label: String(date.toJSON()).substring(0, 10), naoCompareceu: 0, compareceu: 0 })
            const consultingsFromSelectedDate = consultings.filter((el) => String(el.start).substring(0, 10) === date.toJSON().substring(0, 10)) || []
            for (const item of consultingsFromSelectedDate) {
                let el = dadosAv.find((el) => el.label === String(item.start).substring(0, 10))
                if (item.status !== 'agendamento_excluido' || item.status !== 'agendamento_compareceu') {
                    el.naoCompareceu += 1
                }
                if (item.status === 'agendamento_compareceu') {
                    el.compareceu += 1
                }
            }
        }
        showChartAvaliacoes(dadosAv)

        show = false
    }

    function hexToRgba(hex: string, alpha: number) {
        const n = parseInt(hex.replace('#', ''), 16)
        return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
    }

    function currency(val: number) {
        return window.configs.currency + Number(val || 0).toFixed(2)
    }

    // gauge: comparação do faturamento de hoje contra a média dos últimos 3 meses.
    // Chart.js não tem um tipo "radialBar" nativo, então reaproveitamos o mesmo recurso
    // já usado nas outras abas (donut com 2 fatias + plugin de texto central)
    function showChartEvolution(atual, anterior) {
        let valor = (100 / anterior) * atual - 100
        let label = ''
        let cor = '#008FFB'
        let resultado = atual - anterior
        label = atual.toLocaleString(undefined, { minimumFractionDigits: 2 })
        let corSaldo = resultado >= 0 ? '#008FFB' : '#D0483E'
        let isBackward = false
        if (resultado > 0) {
            desempenho = valor.toFixed(0) + '% melhor que os últimos 3 meses'
        } else if (resultado <= -5) {
            isBackward = true
            valor *= -1
            cor = '#D0483E'
            desempenho = valor.toFixed(0) + '% abaixo dos últimos 3 meses'
        } else {
            valor = 0
            desempenho = 'Faturamento'
        }
        if (!Number.isFinite(valor)) desempenho = 'Faturamento'
        valor = Math.max(0, Math.min(100, valor))

        const centerText: Plugin<'doughnut'> = {
            id: 'centerTextEvolution',
            afterDraw(chart) {
                const { ctx, chartArea } = chart
                const x = (chartArea.left + chartArea.right) / 2
                const y = (chartArea.top + chartArea.bottom) / 2
                ctx.save()
                ctx.textAlign = 'center'
                ctx.textBaseline = 'middle'
                ctx.font = '24px sans-serif'
                ctx.fillStyle = corSaldo
                ctx.fillText(label, x, y - 8)
                ctx.font = '13px sans-serif'
                ctx.fillStyle = MUTED
                ctx.fillText('Vendas no mês', x, y + 18)
                ctx.restore()
            },
        }

        const chart = new Chart(chartEvolution, {
            type: 'doughnut',
            data: {
                labels: ['Evolução', 'Restante'],
                datasets: [
                    {
                        data: [valor, 100 - valor],
                        rotation: isBackward ? 360 - (360 / 100) * valor : 0,
                        backgroundColor: [cor, hexToRgba('#999999', 0.2)],
                        borderWidth: 0,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '65%',
                plugins: { legend: { display: false }, tooltip: { enabled: false } },
            },
            plugins: [centerText],
        })
        charts.push(chart)
    }

    function showChartAvaliacoes(dadosAv) {
        const chart = new Chart(chartAvaliacoes, {
            type: 'bar',
            data: {
                labels: dadosAv.map((el) => friendlyDate(el.label)),
                datasets: [
                    { label: 'Avaliação realizada', data: dadosAv.map((el) => el.compareceu), backgroundColor: '#6179F3', borderRadius: 4 },
                    {
                        label: 'Avaliação não realizada',
                        data: dadosAv.map((el) => el.naoCompareceu - el.compareceu),
                        backgroundColor: '#EAB818',
                        borderRadius: 4,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    tooltip: { mode: 'index', intersect: false },
                },
                scales: {
                    x: { stacked: true, grid: { display: false }, ticks: { color: MUTED } },
                    y: {
                        stacked: true,
                        beginAtZero: true,
                        suggestedMax: 5,
                        grid: { color: hexToRgba('#999999', 0.15) },
                        ticks: { color: MUTED, precision: 0 },
                    },
                },
            },
        })
        charts.push(chart)
    }

    // itens mais vendidos: ranking em barra horizontal (mais legível que colunas para 15 nomes)
    function showChartMaisVendidos(items: { label: string; total: number }[]) {
        maisVendidosHeight = Math.max(360, items.length * 28)
        const chart = new Chart(chartMaisVendidos, {
            type: 'bar',
            data: {
                labels: items.map((el) => el.label),
                datasets: [{ label: 'Valor vendido', data: items.map((el) => el.total), backgroundColor: '#379E75', borderRadius: 4 }],
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                // reserva espaço à direita da área de plotagem para o datalabel da barra mais longa não ficar cortado
                layout: { padding: { right: 60 } },
                plugins: {
                    legend: { display: false },
                    tooltip: { callbacks: { label: (ctx) => currency(ctx.parsed.x) } },
                    datalabels: { anchor: 'end', align: 'end', color: MUTED, formatter: (val) => currency(val) },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { color: MUTED } },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    // receita por origem: também ranking em barra horizontal
    function showChartOrigem(items: { label: string; total: number }[]) {
        const chart = new Chart(chartOrigem, {
            type: 'bar',
            data: {
                labels: items.map((el) => el.label),
                datasets: [{ label: 'Valor vendido', data: items.map((el) => el.total), backgroundColor: '#379E75', borderRadius: 4 }],
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                // reserva espaço à direita da área de plotagem para o datalabel da barra mais longa não ficar cortado
                layout: { padding: { right: 60 } },
                plugins: {
                    legend: { display: false },
                    tooltip: { callbacks: { label: (ctx) => currency(ctx.parsed.x) } },
                    datalabels: { anchor: 'end', align: 'end', color: MUTED, formatter: (val) => currency(val) },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { color: MUTED } },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    function dynamicSortMultiple(desc, ...args) {
        var props = args
        return function (obj1, obj2) {
            var i = 0,
                result = 0,
                numberOfProperties = props.length
            while (result === 0 && i < numberOfProperties) {
                result = dynamicSort(desc, props[i])(obj1, obj2)
                i++
            }
            return result
        }
    }

    function dynamicSort(desc, property) {
        let sortOrder = 1
        if (desc) sortOrder = -1
        return function (a, b) {
            var result = a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0
            return result * sortOrder
        }
    }

    function showChartMeioPagamento(dados: { label: string; total: number }[]) {
        const PALETTE = ['#008FFB', '#379E75', '#FEB019', '#775DD0', '#6179F3', '#D0483E', '#9AA0A6']
        const total = dados.reduce((t, el) => t + el.total, 0)
        const chart = new Chart(chartMeioPagamento, {
            type: 'doughnut',
            data: {
                labels: dados.map((el) => el.label),
                datasets: [
                    {
                        data: dados.map((el) => (total > 0 ? Number(((100 / total) * el.total).toFixed(1)) : 0)),
                        backgroundColor: dados.map((_, i) => PALETTE[i % PALETTE.length]),
                        borderColor: 'transparent',
                        borderWidth: 2,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    tooltip: { callbacks: { label: (ctx) => ctx.label + ': ' + ctx.parsed.toFixed(1) + '%' } },
                    datalabels: { color: '#fff', font: { size: 11, weight: 'bold' }, formatter: (val) => (val >= 5 ? val.toFixed(1) + '%' : '') },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    function showChartVendasMes(data: number[], cats: string[]) {
        const chart = new Chart(chartVendasMes, {
            type: 'line',
            data: {
                labels: cats,
                datasets: [
                    {
                        label: 'Valor vendido',
                        data,
                        borderColor: '#6179F3',
                        backgroundColor: hexToRgba('#6179F3', 0.35),
                        fill: true,
                        tension: 0.3,
                        borderWidth: 2,
                        pointRadius: 0,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    legend: { display: false },
                    tooltip: { callbacks: { label: (ctx) => currency(ctx.parsed.y) } },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { beginAtZero: true, grid: { display: false }, ticks: { display: false } },
                },
            },
        })
        charts.push(chart)
    }

    function showChartVendasDia(data: number[], dataPast: number[], cats: string[]) {
        const chart = new Chart(chartVendasDia, {
            data: {
                labels: cats,
                datasets: [
                    { type: 'bar', label: 'Últimos 28 dias', data, backgroundColor: '#008FFB', borderRadius: 4 },
                    {
                        type: 'line',
                        label: 'Período anterior',
                        data: dataPast,
                        borderColor: '#FEB019',
                        backgroundColor: '#FEB019',
                        borderWidth: 2,
                        tension: 0.3,
                        pointRadius: 0,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    tooltip: { callbacks: { label: (ctx) => ctx.dataset.label + ': ' + currency(ctx.parsed.y) } },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED } },
                    y: { grid: { display: false }, ticks: { display: false } },
                },
            },
        })
        charts.push(chart)
    }

    onMount(async () => {
        try {
            show = true
            await loadDashboard()
        } catch (error) {
        } finally {
            show = false
        }
    })

    // conversão: houve venda para a mesma pessoa na data do agendamento, ou em até 30 dias depois
    function wasConverted(appointment, invoices) {
        const start = new Date(appointment.start)
        start.setHours(0, 0, 0, 0)
        const limit = new Date(start)
        limit.setDate(limit.getDate() + 30)
        limit.setHours(23, 59, 59, 999)
        return invoices.some((inv) => inv.personId === appointment.personId && new Date(inv.createdAt) >= start && new Date(inv.createdAt) <= limit)
    }

    function friendlyDate(date) {
        let today = new Date()
        today.setHours(0, 0, 0, 0)
        date = dayjs(date)
        const diff = date.diff(today, 'day')
        if (diff === 0) {
            // hoje
            return 'hoje'
        }
        if (diff === -1) {
            // hoje
            return 'ontem'
        }
        if (diff === 1) {
            // hoje
            return 'amanhã'
        }

        return dayjs(date).utc().format('DD/MM')
    }
</script>

<div class="base-dash sales-dash">
    <ZContainer class="h-space">
        <ZContainerCol size={3} sizeM={12}>
            <div class="-result-label">{desempenho}</div>
            <div class="row -result">
                <div class="-result-graf">
                    <canvas bind:this={chartEvolution}></canvas>
                </div>
            </div>
            <div class="row config-box -result-day">
                <div class="col -box">
                    <div class="-legenda -opaco">ontem</div>
                    <div class="-result-valor -opaco">
                        {faturamentoOntem.toLocaleString(undefined, { style: 'currency', currency: window.configs.currency })}
                    </div>
                </div>
                <div class="col -box">
                    <div class="-legenda">hoje</div>
                    <div class="-result-valor -result-valor-hoje">
                        {faturamentoHoje.toLocaleString(undefined, { style: 'currency', currency: window.configs.currency })}
                    </div>
                </div>
            </div>
        </ZContainerCol>
        <ZContainerCol size={9} sizeM={12}>
            <div class="-avaliacoes">
                <div class="row config-box">
                    <div class="col-12 col-lg-4">
                        <div class="row -av-titulo">Avaliações marcadas para o mês</div>
                        <div class="row -border-bottom">
                            <div class="col -box">
                                <div class="-valor -opaco">{avaliacoesAgendadasAnterior}</div>
                                <div class="-legenda -opaco">mês anterior até dia {new Date().getDate()}</div>
                            </div>
                            <div class="col -box">
                                <div class="-valor">{avaliacoesAgendadasAtual}</div>
                                <div class="-legenda">este mês até hoje</div>
                            </div>
                        </div>
                        <div class="row -av-titulo">Avaliações realizadas</div>
                        <div class="row -border-bottom">
                            <div class="col -box">
                                <div class="-valor -opaco">{avaliacoesRealizadasAnterior}</div>
                                <div class="-legenda -opaco">mês anterior até dia {new Date().getDate()}</div>
                            </div>
                            <div class="col -box">
                                <div class="-valor">{avaliacoesRealizadasAtual}</div>
                                <div class="-legenda">este mês até hoje</div>
                            </div>
                        </div>
                        <div class="row -av-titulo">Avaliações convertidas</div>
                        <div class="row">
                            <div class="col -box">
                                <div class="-valor -opaco">{avaliacoesConvertidasAnterior}</div>
                                <div class="-legenda -opaco">mês anterior até dia {new Date().getDate()}</div>
                            </div>
                            <div class="col -box">
                                <div class="-valor">{avaliacoesConvertidasAtual}</div>
                                <div class="-legenda">este mês até hoje</div>
                            </div>
                        </div>
                    </div>
                    <div class="col-12 col-lg-8 -grafAv">
                        <canvas bind:this={chartAvaliacoes}></canvas>
                    </div>
                </div>
            </div>
        </ZContainerCol>

        <Zframe size={12} sizeM={12} title="Valor vendido por dia">
            <div class="chart-box">
                <canvas bind:this={chartVendasDia}></canvas>
            </div>
        </Zframe>

        <Zframe size={12} sizeM={12} title="Valor vendido por mês">
            <div class="chart-box">
                <canvas bind:this={chartVendasMes}></canvas>
            </div>
        </Zframe>

        <Zframe size={6} sizeM={12} title="Receita pela origem">
            <div class="chart-box chart-box_tall">
                <canvas bind:this={chartOrigem}></canvas>
            </div>
        </Zframe>

        <Zframe size={6} sizeM={12} title="Valor por Meio de pagamento">
            <div class="chart-box chart-box_tall">
                <canvas bind:this={chartMeioPagamento}></canvas>
            </div>
        </Zframe>

        <Zframe size={12} sizeM={12} title="Itens mais vendidos">
            <div class="chart-box" style="height:{maisVendidosHeight}px">
                <canvas bind:this={chartMaisVendidos}></canvas>
            </div>
        </Zframe>
    </ZContainer>
</div>

<LoadSpinner delay={300} {show} />

<style>
    .chart-box {
        position: relative;
        width: 100%;
        height: 320px;
    }
    .chart-box_tall {
        height: 380px;
    }

    /* base-dash */
    .base-dash .row {
        margin-right: 0px;
        margin-left: 0px;
        padding-right: 0px;
        padding-left: 0px;
    }
    .base-dash .-border-bottom {
        border-bottom: 1px solid #99999930;
        padding-bottom: 16px;
    }
    .base-dash .-opaco {
        color: #777 !important;
    }
    .base-dash .-av-titulo {
        font-size: 18px;
        justify-content: center;
        align-items: center;
        color: #888;
        padding-top: 16px;
    }
    .base-dash .-legenda {
        font-size: 14px;
        justify-content: center;
        align-items: center;
        color: #666;
        text-align: center;
    }
    .base-dash .-box {
        border: 0px solid cyan;
        text-align: center;
        padding: 0px;
        padding-left: 0px;
    }
    .base-dash .-box .-valor {
        font-size: 16px;
        color: #333;
        padding: 0px 3px 0px 3px;
        color: #6179f3;
    }
    .base-dash .-box .-legenda {
        padding: 0px;
        color: #6179f3;
    }
    .base-dash .-box > *:last-child {
        padding-bottom: 5px;
    }

    .base-dash .-avaliacoes {
        height: 100%;
    }
    .base-dash .-avaliacoes .-box .-valor {
        font-size: 18px;
        color: #333;
        padding: 0px 3px 0px 3px;
        color: #6179f3;
    }
    .base-dash .-base .-avaliacoes .-box .-legenda {
        padding: 0px;
        color: #6179f3;
    }
    .base-dash .-avaliacoes .config-box {
        flex-grow: 1;
        height: 100%;
    }

    .base-dash .-box {
        flex: 1 1 0;
        text-align: center;
        color: #666;
        border-radius: 4px;
    }
    .base-dash .-result-label {
        text-align: center;
        color: #888;
    }

    .base-dash .-result {
        display: flex;
        justify-content: center;
        align-items: center;
        align-self: center;
        flex: 0 0 auto;
        z-index: 1;
        position: relative;
        margin-top: -25px;
    }
    .base-dash .-result-graf {
        position: relative;
        width: 100%;
        max-height: 280px;
        padding: 25px 5px 5px 5px;
    }
    .base-dash .-result-day {
        padding: 6px;
    }
    .base-dash .-result-day > *:first-child {
        text-align: left;
    }
    .base-dash .-result-day > *:last-child {
        text-align: right;
    }
    .base-dash .-result-valor {
        font-size: 22px;
        flex-grow: 1;
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .base-dash .-result-valor-hoje {
        color: #379e75;
        font-weight: bold;
    }
    .base-dash .by-day-method .config-box {
        height: 100%;
    }
    .base-dash .-grafAv {
        position: relative;
        min-height: 260px;
    }
</style>
